import { useState } from 'react';
import type { PlayMode, Game } from 'app/CasinoGames/types';
import useGameHistoryEntry from './useGameHistoryEntry';
import type { CasinoGamesProps } from './types';

// An opened game is always at hand: a grid click passes it, a /game/:vendor/:slug page gets it
// in the props. Closing keeps the last one.
// `deferred`: the game is shown, but its provider iframe isn't loaded until the visitor asks for it
// (see initialStartParams).
// `scrollIntoView`: a game opened from the grid takes its place at the top of the content, often far
// above where the grid was scrolled to, so the page is brought to the game once it is shown.
type StartParams = { playMode: PlayMode; deferred: boolean; loginRequired: boolean; scrollIntoView: boolean } & (
  | { opened: true; game: Game }
  | { opened: false; game: Game | null }
);

export type GameStarter = StartParams & {
  openRealGame: (game: Game) => void;
  openPracticeGame: (game: Game) => void;
  play: () => void;
  switchMode: () => void;
  close: () => Promise<void>;
};

// A /game/:vendor/:slug page comes with its game resolved by Rails (CasinoGamesProps.game), so
// it opens already loaded - on the server-rendered page too. On a phone that page is reached by a
// direct link or a plain HTML navigation (a crawler, a link opened in a new tab), never by a grid
// click (it opens the game in place, see openRealGame), so the heavy provider iframe waits for a
// "Play" click there: loaded right away, it would drag the mobile page speed metrics (LCP, INP,
// CLS) down, and search engines rank pages by them.
const initialStartParams = ({ game, playMode, loginRequired, common }: CasinoGamesProps): StartParams =>
  game
    ? {
        playMode: playMode ?? 'practice',
        game,
        opened: true,
        deferred: common.mobileVisitor || loginRequired,
        loginRequired,
        scrollIntoView: false,
      }
    : { playMode: 'practice', game: null, opened: false, deferred: false, loginRequired: false, scrollIntoView: false };

const fireGameLaunchedEvent = () => {
  const event = new CustomEvent('gameLaunched');
  document.dispatchEvent(event);
};

// eslint-disable-next-line max-lines-per-function, func-names
const useGameStarter = (props: CasinoGamesProps): GameStarter => {
  const [startParams, setStartParams] = useState<StartParams>(() => initialStartParams(props));
  const hide = () => {
    setStartParams((prev) => ({ ...prev, opened: false }));
  };
  const gameEntry = useGameHistoryEntry(hide);

  const openGame = (game: Game, playMode: PlayMode) => {
    gameEntry.push(game, playMode);
    setStartParams({ game, playMode, opened: true, deferred: false, loginRequired: false, scrollIntoView: true });
    fireGameLaunchedEvent();
  };

  const openRealGame = (game: Game) => openGame(game, 'real');
  const openPracticeGame = (game: Game) => openGame(game, 'practice');

  const play = () => {
    setStartParams((prev) => ({ ...prev, deferred: false }));
  };

  const switchMode = () => {
    const playMode = startParams.playMode === 'real' ? 'practice' : 'real';

    gameEntry.setMode(playMode);
    // Picking a mode is a click on the game too - no second one on "Play" to start it.
    setStartParams((prev) => ({ ...prev, playMode, deferred: false, scrollIntoView: false }));
  };

  // Resolves once the game's history entry is left (see leave).
  const close = () => {
    hide();
    return gameEntry.leave();
  };

  return {
    ...startParams,
    openRealGame,
    openPracticeGame,
    play,
    switchMode,
    close,
  };
};

export default useGameStarter;
